import { Component, ErrorInfo, ReactNode } from 'react';
import { UndrawIllustration } from '@/components/common/UndrawIllustration';
import { NativeButtonWithIcon } from '@/components/common/NativeButtonWithIcon';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
          <div className="max-w-md text-center">
            <UndrawIllustration
              scene="bug-fixing"
              title="Something went wrong"
              className="mx-auto mb-6 h-44 w-full max-w-xs"
            />
            <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
            <p className="mt-2 text-muted-foreground">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <NativeButtonWithIcon
              type="button"
              onClick={() => window.location.reload()}
              icon="refresh-cw"
              className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Reload Page
            </NativeButtonWithIcon>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
